// src/modules/analytics/components/FiltersBar.tsx
import { useState, useEffect } from 'react';
import { Button, Autocomplete, TextField, Stack } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import apiReq from 'core/api';
import type { Project } from 'types/Project';
import type { User } from 'types/User';
import type { AnalyticsFilters } from '../types';

interface Props {
  filters: AnalyticsFilters;
  onChange: (f: AnalyticsFilters) => void;
  onApply: () => void;
  loading: boolean;
}

export function FiltersBar({ filters, onChange, onApply, loading }: Props) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    apiReq<Project[]>('projects')
      .then((d) => setProjects(d ?? []))
      .catch(() => {
        console.error('Error fetching projects');
      });
    apiReq<User[]>('user/all')
      .then((d) => setUsers(d ?? []))
      .catch(() => {
        console.error('Error fetching users');
      });
  }, []);

  const selectedProject = projects.find((p) => p.id === filters.project_id) ?? null;
  const selectedUser = users.find((u) => u.id === filters.user_id) ?? null;

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      flexWrap="wrap"
      sx={{
        p: 1.5,
        background: (t) => t.palette.background.paper,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <TextField
        label="Начало периода"
        type="date"
        size="small"
        value={filters.date_from}
        onChange={(e) => onChange({ ...filters, date_from: e.target.value })}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
      />
      <TextField
        label="Конец периода"
        type="date"
        size="small"
        value={filters.date_to}
        onChange={(e) => onChange({ ...filters, date_to: e.target.value })}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
      />
      <Autocomplete
        size="small"
        options={projects}
        getOptionLabel={(o) => o.name}
        getOptionKey={(o) => o.id ?? o.name}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        value={selectedProject}
        onChange={(_, v) => onChange({ ...filters, project_id: v?.id ?? null })}
        renderInput={(params) => <TextField {...params} label="Проект" />}
        sx={{ minWidth: 200 }}
        clearOnEscape
      />
      <Autocomplete
        size="small"
        options={users}
        getOptionLabel={(o) => o.name || o.email}
        getOptionKey={(o) => o.id ?? o.email}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        value={selectedUser}
        onChange={(_, v) => onChange({ ...filters, user_id: v?.id ?? null })}
        renderInput={(params) => <TextField {...params} label="Пользователь" />}
        sx={{ minWidth: 220 }}
        clearOnEscape
      />
      <Button
        variant="contained"
        onClick={onApply}
        disabled={loading}
        startIcon={<FilterListIcon />}
        sx={{ ml: 'auto !important', fontWeight: 600 }}
      >
        Применить
      </Button>
    </Stack>
  );
}
